import { useCallback, useMemo, useState } from "react";

import { CameraIcon, PhotoIcon } from "@heroicons/react/24/outline";
import { StarIcon } from "@heroicons/react/24/solid";

import useVehicleImages from "../../data/useVehicleImages";
import { IVehicle } from "../../models/Vehicle";
import classNames from "../../utils/classNames";
import StockImageModal, {
  StockLightboxImage,
} from "../stock/viewStock/StockImageModal";

interface VehicleImageCellProps {
  vehicle: IVehicle;
}

// Row-level vehicle image preview. Shows the cached vehicle thumbnail at a
// larger size than before and, on click, loads every attached image for that
// vehicle into the shared StockImageModal carousel.
//
// Note: image fetching is intentionally lazy (click-triggered) — firing a
// `/vehicle/{id}/images` request for every row on mount causes a thundering
// herd of react-query subscriptions that has been linked to a React
// "Maximum update depth exceeded" loop on large pages.
export default function VehicleImageCell({ vehicle }: VehicleImageCellProps) {
  const [open, setOpen] = useState(false);
  const [fetchEnabled, setFetchEnabled] = useState(false);

  const { vehicleImages, downloadDoc } = useVehicleImages(
    vehicle.id,
    fetchEnabled
  );

  const vehicleLabel = useMemo(() => {
    const label = [vehicle.combinedMake, vehicle.combinedModel]
      .filter(Boolean)
      .join(" ");
    return label || vehicle.vrm || "Vehicle image";
  }, [vehicle.combinedMake, vehicle.combinedModel, vehicle.vrm]);

  const donorImageDocs = useMemo(() => {
    const docs = vehicleImages.data;
    if (!Array.isArray(docs)) return [];
    return docs.filter(
      (d) =>
        !!d.thumbnail && !(d.contentType && d.contentType.includes("video"))
    );
  }, [vehicleImages.data]);

  const lightboxImages: StockLightboxImage[] = useMemo(() => {
    const list: StockLightboxImage[] = donorImageDocs.map((doc, i) => ({
      id: doc.id,
      url: `data:${doc.contentType ?? "image/png"};base64,${doc.thumbnail}`,
      alt: doc.fileName ?? `${vehicleLabel} photo ${i + 1}`,
    }));
    if (list.length === 0 && vehicle.vehicleImageUrl) {
      list.push({ url: vehicle.vehicleImageUrl, alt: vehicleLabel });
    }
    return list;
  }, [donorImageDocs, vehicle.vehicleImageUrl, vehicleLabel]);

  const resolveFullUrl = useCallback(
    (img: StockLightboxImage): Promise<string | null> =>
      new Promise((resolve) => {
        if (!img.id) {
          resolve(null);
          return;
        }
        downloadDoc.mutate(img.id, {
          onSuccess: (file: unknown) => {
            try {
              const url = window.URL.createObjectURL(file as Blob);
              resolve(url);
            } catch {
              resolve(null);
            }
          },
          onError: () => resolve(null),
        });
      }),
    [downloadDoc]
  );

  // Prefer a real donor photo (has an `id` because it came from the images
  // endpoint) once loaded. Otherwise fall back to the row's cached
  // vehicleImageUrl — which is the UKVD catalogue render.
  const firstDonorDoc = donorImageDocs[0];
  const donorThumbnailUrl = firstDonorDoc
    ? `data:${firstDonorDoc.contentType ?? "image/png"};base64,${firstDonorDoc.thumbnail}`
    : null;
  const thumbnailUrl = donorThumbnailUrl || vehicle.vehicleImageUrl || null;

  // UKVD catalogue shots are side-on renders that can sit close to the canvas
  // edges — `object-contain` with a max-size constraint keeps the whole car in
  // frame. Donor photos are full-bleed photography so `object-cover` gives a
  // better tile fill.
  const isDonorThumbnail = !!donorThumbnailUrl;

  const imageCount = vehicle.vehicleImagesCount ?? 0;
  const hasAttachedImages = imageCount > 0;
  const moreCount = Math.max(
    0,
    (donorImageDocs.length || imageCount) - 1
  );

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFetchEnabled(true);
    setOpen(true);
  };

  const handlePrefetch = () => {
    if (hasAttachedImages && !fetchEnabled) {
      setFetchEnabled(true);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        onMouseEnter={handlePrefetch}
        onFocus={handlePrefetch}
        aria-label={`Open ${vehicleLabel} images`}
        className={classNames(
          // Sized to fill the matching card frame in the parent cell. The
          // RowCard-style outer frame is applied in `AwaitingInventoryTable`
          // so every row cell shares the exact same chrome.
          "group relative block h-full min-h-[7rem] w-full overflow-hidden rounded-md bg-slate-100",
          "transition-all hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-primary-400"
        )}
      >
        {thumbnailUrl ? (
          isDonorThumbnail ? (
            <img
              src={thumbnailUrl}
              alt={vehicleLabel}
              className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <img
                src={thumbnailUrl}
                alt={vehicleLabel}
                className="block object-contain"
                style={{ maxWidth: "70%", maxHeight: "70%" }}
              />
            </div>
          )
        ) : hasAttachedImages ? (
          <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-slate-500">
            <PhotoIcon className="h-8 w-8" aria-hidden="true" />
            <span className="text-[10px] font-semibold uppercase tracking-wide">
              {imageCount} photo{imageCount === 1 ? "" : "s"}
            </span>
            <span className="text-[10px] text-slate-400">Click to view</span>
          </div>
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-slate-400">
            <CameraIcon className="h-8 w-8" aria-hidden="true" />
            <span className="text-[10px] font-medium uppercase tracking-wide">
              No photo
            </span>
          </div>
        )}

        {vehicle.vehiclePremium && (
          <span className="absolute left-1 top-1 inline-flex items-center gap-0.5 rounded-full bg-amber-500/95 px-1.5 py-0.5 text-[10px] font-semibold text-white shadow-sm">
            <StarIcon className="h-3 w-3" aria-hidden="true" />
            Premium
          </span>
        )}

        {moreCount > 0 && thumbnailUrl && (
          <span className="absolute bottom-1 right-1 inline-flex items-center gap-1 rounded-full bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
            <PhotoIcon className="h-3 w-3" aria-hidden="true" />
            {`+${moreCount}`}
          </span>
        )}
      </button>

      {open && (
        <StockImageModal
          open={open}
          setOpen={setOpen}
          images={lightboxImages}
          title={vehicleLabel}
          resolveFullUrl={resolveFullUrl}
        />
      )}
    </>
  );
}
